/**
 * Chamadas HTTP dos lançamentos de residência.
 *
 * Papel: aqui ficam as quatro chamadas de `/api/residencias/lancamentos` que a
 * tela usa — `buscarLancamentos`, `criarLancamento`, `atualizarLancamento` e
 * `excluirLancamento`.
 *
 * Como usar: `buscarLancamentos` devolve as casas de topo junto com os
 * lançamentos, e é dela que a tela tira os blocos e as opções do `<select>`,
 * porque quem tem `residencias` sem `cadastros-base` não alcança
 * `/api/residencias`. Gravar responde 409 com a mensagem da API quando a casa
 * escolhida fica dentro de outra ou deixou de existir.
 */
import {
  type LancamentoResidencia,
  type LancamentosDasResidencias,
  type NovoLancamentoResidencia,
  lancamentoResidenciaSchema,
  lancamentosDasResidenciasSchema,
} from '@concierge/shared';

import { clienteApi } from '@/shared/api';

/**
 * Lê as casas de topo e todos os lançamentos.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As casas de topo, em qualquer ordem, e os lançamentos na ordem de
 *   criação.
 */
export function buscarLancamentos(sinal?: AbortSignal): Promise<LancamentosDasResidencias> {
  return clienteApi.get('/residencias/lancamentos', {
    schema: lancamentosDasResidenciasSchema,
    sinal,
  });
}

/**
 * Cria um lançamento.
 *
 * @param dados - O lançamento já passado por `novoLancamentoResidenciaSchema`.
 * @returns O lançamento como ficou gravado, com a casa aninhada.
 */
export function criarLancamento(
  dados: NovoLancamentoResidencia,
): Promise<LancamentoResidencia> {
  return clienteApi.post('/residencias/lancamentos', dados, {
    schema: lancamentoResidenciaSchema,
  });
}

/**
 * Substitui todos os campos de um lançamento.
 *
 * @param id - Identificador da linha em `lancamentos_residencia`.
 * @param dados - O lançamento inteiro, e não apenas os campos alterados.
 * @returns O lançamento como ficou gravado.
 */
export function atualizarLancamento(
  id: string,
  dados: NovoLancamentoResidencia,
): Promise<LancamentoResidencia> {
  return clienteApi.put(`/residencias/lancamentos/${encodeURIComponent(id)}`, dados, {
    schema: lancamentoResidenciaSchema,
  });
}

/**
 * Apaga um lançamento.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `lancamentos_residencia`.
 */
export function excluirLancamento(id: string): Promise<void> {
  return clienteApi.remover(`/residencias/lancamentos/${encodeURIComponent(id)}`);
}
